import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';

/**
 * QuestionText - Renders survey question text with basic formatting support
 *
 * Supports:
 * - Line breaks (single Enter creates a line break)
 * - Literal \n in text creates a line break
 * - **Bold text**
 * - *Italic text*
 * - Bullet lists (- item)
 * - Numbered lists (1. item)
 */

/**
 * Preprocess text to handle line breaks properly:
 * 1. Convert literal \n strings to actual newlines
 * 2. Convert single newlines to Markdown line breaks (two trailing spaces)
 */
const preprocessText = (text: string): string => {
  if (!text) return '';

  // First, convert literal \n strings to actual newlines
  let processed = text.replace(/\\n/g, '\n');

  // Convert single newlines to Markdown line breaks (two spaces + newline)
  // This makes single Enter/Shift+Enter work as line breaks
  // But preserve double newlines as paragraph breaks
  processed = processed.replace(/\n/g, '  \n');

  return processed;
};

const components: Components = {
  p: ({ children }) => <span className="block mb-2 last:mb-0">{children}</span>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  // Disable other elements for safety
  a: ({ children }) => <span>{children}</span>,
  code: ({ children }) => <span>{children}</span>,
  pre: ({ children }) => <span>{children}</span>,
  blockquote: ({ children }) => <span>{children}</span>,
  h1: ({ children }) => <span className="font-bold">{children}</span>,
  h2: ({ children }) => <span className="font-bold">{children}</span>,
  h3: ({ children }) => <span className="font-bold">{children}</span>,
};

interface QuestionTextProps {
  children: string;
  className?: string;
}

const QuestionText: React.FC<QuestionTextProps> = ({ children, className = '' }) => {
  if (!children) return null;

  // Preprocess text to handle line breaks properly
  const processedText = preprocessText(children);

  return (
    <div className={className}>
      <ReactMarkdown components={components}>{processedText}</ReactMarkdown>
    </div>
  );
};

export default QuestionText;
